import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Mail, Phone, Calendar } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCalc } from "@/context/CalcContext";
import { BOOKING_URL } from "@/constants/booking";
import useSEO from "@/hooks/useSEO";

export default function ThankYou() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const { lead, results } = useCalc();
  const isEn = i18n.language?.startsWith("en");

  useSEO({
    title: t("thankyou.seo_title"),
    description: isEn
      ? "Your PDF report is on its way. Book your complimentary 60-min framing session with Ariovis to turn this ROI into a Netwrix Identity Manager rollout plan."
      : "Votre étude PDF est en route. Réservez votre cadrage 60 min offert avec Ariovis pour transformer ce ROI en plan de déploiement Netwrix Identity Manager.",
  });

  const nfmt = new Intl.NumberFormat(isEn ? "en-US" : "fr-FR", { maximumFractionDigits: 0 });

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      <Header minimal />

      <div className="ar-blob bg-fuchsia-500" style={{ width: 360, height: 360, top: -80, right: -80, opacity: 0.18 }} />
      <div className="ar-blob bg-orange-400" style={{ width: 300, height: 300, bottom: 200, left: -120, opacity: 0.15 }} />

      <div className="relative max-w-4xl mx-auto px-6 lg:px-12 pt-20 pb-24">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-slate-900 mx-auto flex items-center justify-center ar-glow">
            <Check className="w-9 h-9 text-white" strokeWidth={3} />
          </div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mt-8 mb-3">{t("thankyou.eyebrow")}</div>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("thankyou.title_1")}{lead?.firstname ? `, ${lead.firstname}` : ""}. <br />
            <span className="ar-gradient-text">{t("thankyou.title_2")}</span>
          </h1>
          <p className="text-slate-600 mt-6 text-lg max-w-2xl mx-auto">
            {isEn
              ? <>The Ariovis-branded PDF has just been sent to <b className="text-slate-900">{lead?.email || "your address"}</b>. Also check your spam folder — the sender is <b>roi-calculator@ariovis.fr</b>.</>
              : <>Le PDF brandé Ariovis vient d'être envoyé à <b className="text-slate-900">{lead?.email || "votre adresse"}</b>. Vérifiez aussi votre dossier spam — l'expéditeur est <b>roi-calculator@ariovis.fr</b>.</>}
          </p>
        </div>

        <div className="bg-slate-950 text-white rounded-3xl p-10 relative overflow-hidden">
          <div className="ar-grid-circuits absolute inset-0 opacity-30" />
          <div className="relative">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-3">
              {isEn ? "Next step — complimentary session" : "Prochaine étape — session offerte"}
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-4">
              {isEn ? "60 minutes to turn your study into an action plan." : "60 minutes pour transformer votre étude en plan d'action."}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8 max-w-2xl">
              {isEn
                ? "During this framing session, Ariovis consolidates your assumptions with your real data (ITSM / HRIS / IAM), refines application priorities and proposes a rollout schedule with Netwrix Identity Manager."
                : "Lors de cette session de cadrage, Ariovis consolide vos hypothèses avec vos données réelles (ITSM / HRIS / IAM), affine les priorités applicatives et propose un planning de déploiement avec Netwrix Identity Manager."}
            </p>

            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thanks-book-calendar"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full ar-gradient-bg text-white font-semibold tracking-tight transition-all hover:opacity-95 ar-glow mb-8"
            >
              <Calendar className="w-5 h-5" />
              {isEn ? "Book my 60-min framing — direct slot" : "Réserver mon cadrage 60 min — créneau direct"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>

            <div className="grid md:grid-cols-2 gap-4">
              <a
                href="mailto:matthieu.filizzola@ariovis.fr?subject=Cadrage%20IAM%20%E2%80%94%20suite%20%C3%A0%20mon%20%C3%A9tude%20ROI"
                data-testid="thanks-email-matthieu"
                className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-5 hover:border-fuchsia-600 transition"
              >
                <div className="w-11 h-11 rounded-xl bg-fuchsia-600 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-wider text-slate-400 uppercase">
                    {isEn ? "Direct contact" : "Contact direct"}
                  </div>
                  <div className="font-semibold">matthieu.filizzola@ariovis.fr</div>
                </div>
              </a>
              <a
                href="tel:+33769509418"
                data-testid="thanks-phone"
                className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-5 hover:border-fuchsia-600 transition"
              >
                <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-wider text-slate-400 uppercase">Matthieu Filizzola</div>
                  <div className="font-semibold">+33 7 69 50 94 18</div>
                </div>
              </a>
            </div>

            <div className="mt-6 text-sm text-slate-400">
              {isEn ? "Project team — " : "Équipe Projets — "}
              <a className="text-fuchsia-400 hover:text-fuchsia-300" href="mailto:contact-projets@ariovis.fr">contact-projets@ariovis.fr</a>
            </div>
          </div>
        </div>

        {results && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <RecallKpi label={isEn ? "Base ROI" : "ROI central"} value={`${results.central.financials.roi_pct.toFixed(1)} %`} />
            <RecallKpi label={t("results.kpi.payback")} value={results.central.financials.payback_months ? `${results.central.financials.payback_months} ${t("common.months")}` : "—"} />
            <RecallKpi label={isEn ? "NPV" : "VAN"} value={nfmt.format(results.central.financials.net_benefit) + " €"} />
          </div>
        )}

        <div className="flex justify-center mt-10">
          <button
            onClick={() => nav("/")}
            data-testid="thanks-back-home"
            className="group inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition"
          >
            {t("thankyou.cta_home")}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function RecallKpi({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">{label}</div>
      <div className="text-2xl font-bold ar-gradient-text">{value}</div>
    </div>
  );
}
